/**
 * 브라우저가 procedure 를 부르는 **유일한 legal 통로.** (docs/decisions/0008 blocking ①)
 *
 * ─── 이 패키지가 따로 있는 이유 ───
 * `packages/rpc-kernel` 은 `@orpc/server` 와 postgres 를 물고 있어 브라우저에 들어갈 수 없고,
 * `packages/web-sdk` 와 합치면 **@orpc 를 아는 패키지가 곧 vertical 이 import 하는 패키지**가 된다 —
 * 그 순간 raw client 가 재export 되고 "링크는 한 곳"이 깨진다.
 * `.dependency-cruiser.cjs` 의 두 룰이 이 문장을 사실로 만든다:
 * `orpc-only-in-rpc-kernel`(from allowlist 에 이 패키지 한 줄) +
 * `rpc-client-knows-only-orpc-client`(안에서 다시 좁힌다 — `@orpc/server` 는 여기서도 금지다).
 *
 * ─── 클라이언트 타입을 여기서 만들지 않는다 ───
 * `RouterClient<AppRouter>` 는 `@orpc/server` 의 타입이라 이 패키지가 이름을 부를 수 없다.
 * 그래서 `createRpcClient` 는 **호출자가 준 타입을 그대로 돌려주는** 제네릭이고,
 * 그 타입은 커널이 `ClientOf<T>` 로 내보낸다(type-only 통로 하나로 apps/web 까지 간다).
 * 타입 하나 때문에 경계를 열지 않는 것이 요점이다.
 */
import { createORPCClient, type ClientContext, type NestedClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import { RPC_PREFIX, readStamp, type ObservedStamp } from '@worhythm/contracts';
import { createEmitter, type Emitter } from './emitter.js';
import { classifyRpcError, markNoResponse, type RpcFailure } from './errors.js';

/**
 * 배포 스큐 — 이 세션이 처음 본 지문과 다른 지문이 왔다.
 *
 * 클라이언트가 들고 있는 매니페스트·라우트 표는 **그 지문의 배포**에서 온 것이다.
 * 지문이 바뀌었다는 것은 그 표가 지금 서버가 서비스하는 것과 다를 수 있다는 뜻이고,
 * 증상은 에러가 아니라 "메뉴에는 있는데 404" 처럼 조용하다.
 */
export interface SkewEvent {
  readonly field: 'evalHash' | 'surfaceHash';
  readonly baseline: string;
  readonly observed: string;
}

export interface RpcEvents {
  /** 401. **분류일 뿐 정책이 아니다** — 무엇을 할지는 `<SessionWatcher>` 가 정한다. */
  sessionLost: RpcFailure;
  skew: SkewEvent;
  /** 모든 응답의 스탬프. 스큐 판정을 밖에서 다시 하고 싶을 때가 아니라 진단용이다. */
  stamp: ObservedStamp;
}

export interface RpcClientOptions {
  /**
   * RPC 가 사는 출처. 기본은 **동일 출처**다 — dev 는 Vite proxy 가, 프로덕션은 Caddy 가
   * RPC 프리픽스를 API 로 보낸다(STACK.md §3). 그래서 CORS 미들웨어가 이 리포에 0건이다.
   */
  readonly baseUrl?: string;
  /** 요청마다 붙일 헤더. 세션 어댑터가 붙기 전 개발 헤더가 이 자리로 들어온다. */
  readonly headers?: () => Record<string, string>;
  /** 테스트가 진짜 소켓 없이 이 통로를 밟기 위한 자리. 프로덕션 코드는 넘기지 않는다. */
  readonly fetch?: typeof globalThis.fetch;
}

export interface RpcClient<TClient> {
  readonly client: TClient;
  readonly events: Pick<Emitter<RpcEvents>, 'on'>;
  /**
   * 스큐 판정의 기준선. 기본값은 **처음 본 응답**이다 —
   * 클라이언트가 들고 있는 표가 그 응답과 함께 온 것이기 때문이다.
   * `session.bootstrap` 이 생기면(§구현 순서 11) 부트가 이 함수로 자기 지문을 심는다.
   */
  setStampBaseline(stamp: Pick<ObservedStamp, 'evalHash' | 'surfaceHash'>): void;
  /** 마지막으로 본 스탬프. 진단·테스트용이고 판정은 이벤트로 한다. */
  lastStamp(): ObservedStamp | undefined;
}

/**
 * `TClient` 는 호출자가 준다(위 "클라이언트 타입을 여기서 만들지 않는다").
 * 링크·인터셉터·스탬프 관찰이 전부 여기 한 곳에 있으므로, 통로가 둘로 갈라질 자리가 없다.
 */
/**
 * RPC 의 **절대 URL.**
 *
 * ─── 왜 `${baseUrl ?? ''}${RPC_PREFIX}` 가 아니었나 (2026-09-02 브라우저에서 밟았다) ───
 * 동일 출처라 상대 경로면 된다고 생각했는데, `RPCLink` 는 그 문자열에 `new URL(url)` 을 부르고
 * **상대 문자열은 거기서 TypeError 를 던진다.** 그 TypeError 는 `classifyRpcError` 가
 * `offline` 으로 분류하므로 화면에는 "서버에서 응답을 받지 못했습니다"가 뜬다 —
 * 서버는 멀쩡하고 요청은 **나가지도 않았다.** 네트워크 탭에도 아무것도 없어서
 * "서버가 죽었나"부터 확인하게 되는, 이 리포가 가장 싫어하는 형태의 조용한 실패다.
 *
 * 안 보였던 이유가 더 중요하다: **테스트가 전부 절대 `baseUrl` 을 넘긴다.**
 * 기본값 경로를 밟는 테스트가 0개였고, 게이트 8종은 전부 초록이었다.
 * 그래서 아래 테스트 하나가 "baseUrl 없이 만든 클라이언트"를 명시적으로 밟는다.
 *
 * 출처를 못 정하면 **던진다.** 빈 문자열로 진행하면 위 실패로 되돌아가고,
 * 그때는 원인이 다시 감춰진다.
 */
export function resolveRpcUrl(baseUrl: string | undefined): string {
  /* 앱(RN)에는 `location` 이 없다 — DOM 없는 타입으로 읽는다. 앱은 언제나 `baseUrl` 을 준다. */
  const origin = baseUrl ?? (globalThis as { location?: { origin?: string } }).location?.origin;
  if (!origin) {
    throw new Error(
      'RPC 출처를 정할 수 없다. 브라우저 밖에서는 `baseUrl` 을 넘겨야 한다 — ' +
        `상대 경로('${RPC_PREFIX}')는 RPCLink 의 new URL() 에서 TypeError 가 되고, ` +
        '그 TypeError 는 "서버에서 응답을 받지 못했습니다"로 보인다.',
    );
  }
  return new URL(RPC_PREFIX, origin).toString();
}

export function createRpcClient<TClient extends NestedClient<ClientContext>>(
  opts: RpcClientOptions = {},
): RpcClient<TClient> {
  const events = createEmitter<RpcEvents>();
  const doFetch = opts.fetch ?? globalThis.fetch;

  let baseline: { evalHash?: string; surfaceHash?: string } | undefined;
  let last: ObservedStamp | undefined;

  function observe(stamp: ObservedStamp): void {
    last = stamp;
    events.emit('stamp', stamp);
    if (!baseline) {
      baseline = { evalHash: stamp.evalHash, surfaceHash: stamp.surfaceHash };
      return;
    }
    for (const field of ['evalHash', 'surfaceHash'] as const) {
      const was = baseline[field];
      const now = stamp[field];
      // 한쪽이라도 없으면 판정하지 않는다. 없는 헤더를 '변경 없음'이 아니라 '모른다'로 읽는다 —
      // 프록시가 헤더를 떨어뜨린 배포에서 매 요청이 스큐로 보이는 것을 막는다.
      if (was && now && was !== now) events.emit('skew', { field, baseline: was, observed: now });
    }
  }

  const link = new RPCLink({
    url: resolveRpcUrl(opts.baseUrl),
    headers: () => opts.headers?.() ?? {},
    /**
     * 응답이 지나는 **유일한 지점**이라 스탬프를 여기서 읽는다.
     * 인터셉터(아래)는 에러만 보므로 200 응답의 헤더를 못 본다 —
     * 스큐는 200 에서도 온다(오히려 대부분 그렇다).
     */
    fetch: async (request, init) => {
      /* 여기서 던진 것은 **응답이 없는 실패**다 — 화면 경계가 버그와 가르는 표식(errors.ts). */
      const res = await doFetch(request, init).catch((e: unknown) => {
        throw markNoResponse(e);
      });
      observe(readStamp(res.headers));
      return res;
    },
    interceptors: [
      async ({ next }) => {
        try {
          return await next();
        } catch (e) {
          const failure = classifyRpcError(e);
          // 여기서 하는 일은 **알리는 것뿐**이다. 리다이렉트도, 캐시 비우기도 하지 않는다.
          if (failure.kind === 'unauthenticated') events.emit('sessionLost', failure);
          throw e;
        }
      },
    ],
  });

  return {
    client: createORPCClient<TClient>(link),
    events: { on: events.on },
    setStampBaseline(stamp) {
      baseline = { evalHash: stamp.evalHash, surfaceHash: stamp.surfaceHash };
    },
    lastStamp: () => last,
  };
}
